import * as XLSX from 'xlsx';

// Excel serial date to JS Date
function excelDateToJS(serial) {
  if (!serial || isNaN(serial)) return null;
  const utc_days = Math.floor(serial - 25569);
  const utc_value = utc_days * 86400 * 1000;
  return new Date(utc_value);
}

function daysBetween(date1, date2) {
  if (!date1 || !date2) return null;
  return Math.floor((date2 - date1) / (1000 * 60 * 60 * 24));
}

// ─── OPEN JOBS PARSER ────────────────────────────────────────────────────────
// SCM Profit export: OFFICE, CLIENT FULLNAME, BOOKING NUMBER, JOB#,
// JOB TYPE, JOB CREATEDON, EST REV AMOUNT, ACT REV AMOUNT,
// EST COST AMOUNT, ACT COST AMOUNT, P/L AMOUNT, BASE CURRENCY, JOB STATUS
export function parseOpenJobs(buffer) {
  const wb = XLSX.read(buffer, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

  // Find header row
  let headerIdx = rows.findIndex(r =>
    r.some(c => String(c).toUpperCase().includes('JOB#') ||
                String(c).toUpperCase().includes('JOB #') ||
                String(c).toUpperCase().includes('BOOKING NUMBER'))
  );
  if (headerIdx === -1) headerIdx = 0;

  const headers = rows[headerIdx].map(h => String(h).trim().toUpperCase());
  const col = (name) => headers.findIndex(h => h.includes(name));

  const COL = {
    office:     col('OFFICE'),
    client:     col('CLIENT'),
    booking:    col('BOOKING'),
    job:        col('JOB#') !== -1 ? col('JOB#') : col('JOB #'),
    jobType:    col('JOB TYPE'),
    createdOn:  col('CREATEDON') !== -1 ? col('CREATEDON') : col('CREATED'),
    estRev:     col('EST REV'),
    actRev:     col('ACT REV'),
    estCost:    col('EST COST'),
    actCost:    col('ACT COST'),
    pl:         col('P/L'),
    currency:   col('CURRENCY'),
    status:     col('STATUS'),
  };

  const today = new Date();
  const jobs = [];

  for (let i = headerIdx + 1; i < rows.length; i++) {
    const r = rows[i];
    if (!r[COL.job] && !r[COL.client]) continue;

    const createdSerial = parseFloat(r[COL.createdOn]);
    const createdDateObj = excelDateToJS(createdSerial);
    const createdDate = createdDateObj ? createdDateObj.getTime() : null; // store as ms timestamp, not Date
    const daysOpen = createdDateObj ? daysBetween(createdDateObj, today) : null;

    const estRev = parseFloat(r[COL.estRev]) || 0;
    const actRev = parseFloat(r[COL.actRev]) || 0;
    const actCost = parseFloat(r[COL.actCost]) || 0;
    const pl = parseFloat(r[COL.pl]) || 0;
    const gpPct = actRev > 0 ? (pl / actRev) * 100 : (estRev > 0 ? null : null);

    jobs.push({
      office:      String(r[COL.office] || '').trim(),
      client:      String(r[COL.client] || '').trim(),
      bookingNo:   String(r[COL.booking] || '').trim(),
      jobNo:       String(r[COL.job] || '').trim(),
      jobType:     String(r[COL.jobType] || '').trim(),
      createdDate,
      daysOpen,
      estRev,
      actRev,
      actCost,
      pl,
      gpPct,
      currency:    String(r[COL.currency] || 'AED').trim(),
      status:      String(r[COL.status] || '').trim(),
      // Derived
      hasRevenue:  actRev > 0,
      isStale:     daysOpen !== null && daysOpen > 60,
      lagFlag:     daysOpen !== null && daysOpen > 30,
    });
  }

  return jobs;
}

// ─── AR AGING PARSER ─────────────────────────────────────────────────────────
// Tally export: Particulars, Pending Bills, <60, 60-90, 90-180, 180-365, >365
export function parseARaging(buffer) {
  const wb = XLSX.read(buffer, { type: 'array' });
  // Find the Sundry Debtors sheet
  const sheetName = wb.SheetNames.find(n =>
    n.toLowerCase().includes('debtor') || n.toLowerCase().includes('ar')
  ) || wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

  // Find the data rows — look for "Particulars" header
  rows.findIndex(r => // eslint-disable-line
    r.some(c => String(c).toLowerCase().includes('particulars'))
  );

  // The Tally format has two header rows — find the one with bucket labels
  // Buckets: Pending Bills | <60 days | 60 to 90 | 90 to 180 | 180 to 365 | >365 | On Account
  // Each bucket has Debit + Credit columns
  // We need to find column positions by scanning header rows

  const ar = [];
  let inData = false; // eslint-disable-line

  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const firstCell = String(r[0] || '').trim();

    // Skip header rows and company info
    if (firstCell === '' || firstCell.toLowerCase().includes('lp logistics') ||
        firstCell.toLowerCase().includes('particulars') ||
        firstCell.toLowerCase().includes('sundry') ||
        firstCell.toLowerCase().includes('group out') ||
        firstCell.toLowerCase().includes('pending') ||
        firstCell.toLowerCase().includes('debit') ||
        firstCell.toLowerCase().includes('trn') ||
        firstCell.toLowerCase().includes('ids building') ||
        firstCell.toLowerCase().includes('po box') ||
        firstCell.toLowerCase().includes('emirate') ||
        firstCell.toLowerCase().includes('1-jan') ||
        firstCell.toLowerCase().includes('1-apr')) continue;

    if (firstCell.toLowerCase().includes('grand total')) break;

    // Try to extract numeric values — Tally format is wide
    // Col 0 = client name, then pairs of debit/credit for each bucket
    const nums = r.slice(1).map(v => parseFloat(v) || 0);

    // Tally AR format (from our files):
    // cols after name: pending_dr, pending_cr, lt60_dr, lt60_cr, 60_90_dr, 60_90_cr,
    //                  90_180_dr, 90_180_cr, 180_365_dr, 180_365_cr, gt365_dr, gt365_cr, onacct_dr, onacct_cr
    if (nums.length >= 2 && (nums[0] !== 0 || nums[1] !== 0 || nums[2] !== 0)) {
      const pending = nums[0] - nums[1];
      if (Math.abs(pending) < 0.01 && nums.every(n => n === 0)) continue;

      ar.push({
        client:    firstCell,
        pending:   nums[0] - nums[1],
        lt60:      nums[2] - nums[3],
        d60_90:    nums[4] - nums[5],
        d90_180:   nums[6] - nums[7],
        d180_365:  nums[8] - nums[9],
        gt365:     nums[10] - nums[11],
        onAccount: nums[12] !== undefined ? nums[12] - (nums[13] || 0) : 0,
      });
    }
  }

  return ar.filter(r => r.client && Math.abs(r.pending) > 1);
}

// ─── CASHFLOW PARSER ─────────────────────────────────────────────────────────
// Column layout (0-indexed):
//   Col 0: label
//   Col 1: total outstanding (for client/vendor rows)
//   Col 2: same as col 1 (duplicate)
//   Col 3: current period total (for summary rows like opening/receipts/payments/closing)
//   Cols 4-15: weekly forecast (3 months × 4 weeks)
//
// Key rows:
//   Row 1: month dates at cols 4, 8, 12
//   Row 2: week labels "1st week"..."4th week" repeating
//   Row 3: opening balance (col 3 = current, cols 4-15 = weekly opening)
//   Receipts section: total row has cols 3-15, client rows have col1=total, cols 4-15=weekly
//   Payments section: same structure
//   Closing balance row: col 3 = current, cols 4-15 = weekly closing (col 4 duplicates col 3)
export function parseCashflow(buffer) {
  const wb = XLSX.read(buffer, { type: 'array' });
  const sheetName = wb.SheetNames.find(n =>
    n.toLowerCase() === 'cashflow' ||
    n.toLowerCase().includes('cash flow') ||
    n.toLowerCase().includes('cashflow')
  ) || wb.SheetNames[0];

  const ws = wb.Sheets[sheetName];
  // Do NOT use cellDates — keeps serial numbers which XLSX.js handles consistently
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });

  const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };

  // Weekly data is always at cols 4-15 (12 columns = 3 months × 4 weeks)
  const WEEK_START_COL = 4;
  const NUM_WEEKS = 12;

  const getWeekly = (row) =>
    Array.from({ length: NUM_WEEKS }, (_, i) => num(row?.[WEEK_START_COL + i]));

  // ── Detect currency ───────────────────────────────────────────────────────
  const currency = String(rows[0]?.[0] || '').includes('SAR') ? 'SAR' : 'AED';

  // ── Build month/week labels ───────────────────────────────────────────────
  // Row 1: serial number dates at cols 4, 8, 12
  // Convert Excel serial to month name
  const excelSerialToMonth = (serial) => {
    if (!serial) return '';
    const n = parseFloat(serial);
    if (isNaN(n)) return '';
    // Excel serial: days since 1900-01-01 (with leap year bug)
    const date = new Date((n - 25569) * 86400 * 1000);
    return date.toLocaleString('en-US', { month: 'short', year: '2-digit' });
  };

  const monthRow = rows[1] || [];
  const weekRow = rows[2] || [];
  const months = [
    excelSerialToMonth(monthRow[4]),
    excelSerialToMonth(monthRow[8]),
    excelSerialToMonth(monthRow[12]),
  ];

  const weekLabels = Array.from({ length: NUM_WEEKS }, (_, i) => {
    const monthIdx = Math.floor(i / 4);
    const weekLabel = String(weekRow[WEEK_START_COL + i] || `W${i + 1}`);
    return months[monthIdx] ? `${months[monthIdx]} ${weekLabel}` : weekLabel;
  });

  // ── Find key rows by scanning ─────────────────────────────────────────────
  let openingRow = -1, receiptsRow = -1, paymentsRow = -1, closingRow = -1;

  for (let i = 0; i < Math.min(rows.length, 15); i++) {
    const c0 = String(rows[i]?.[0] || '').trim().toUpperCase();
    if (openingRow === -1 && c0.includes('OPENING BALANCE')) openingRow = i;
    if (receiptsRow === -1 && c0.startsWith('RECEIPTS')) receiptsRow = i;
  }
  for (let i = (receiptsRow > 0 ? receiptsRow : 0) + 1; i < rows.length; i++) {
    const c0 = String(rows[i]?.[0] || '').trim().toUpperCase();
    if (paymentsRow === -1 && c0.startsWith('PAYMENTS') &&
        !c0.includes('AS BOOKS') && !c0.includes('IN FIRST') && !c0.includes('INDIRECT')) {
      paymentsRow = i;
    }
    if (c0 === 'CLOSING BALANCE') { closingRow = i; break; }
  }

  if (receiptsRow === -1 || paymentsRow === -1) {
    return { weeks: [], openingBalance: 0, closingBalance: 0, receipts: [], payments: [], currency };
  }

  // ── Opening balance: col 3 = current ─────────────────────────────────────
  const openingBalance = num(rows[openingRow]?.[3]);

  // ── Closing balance: col 3 = current, cols 5-15 = weekly (col 4 duplicates col 3) ──
  // So weekly closing = [col3, col5, col6, ..., col15] — skip col4
  const closingRow_ = rows[closingRow] || [];
  const closingBalance = num(closingRow_[3]);
  // Weekly closing: current period + cols 5-15 (11 values) — pad to 12
  const closingWeekly = [
    num(closingRow_[3]),  // week 1 = current
    ...Array.from({ length: 11 }, (_, i) => num(closingRow_[5 + i])),
  ];

  // ── Receipts and payments weekly totals (cols 4-15 from their header rows) ──
  const receiptsTotals = getWeekly(rows[receiptsRow]);
  const paymentsTotals = getWeekly(rows[paymentsRow]);

  // ── Weekly opening = closing of previous week ─────────────────────────────
  // Use closing row for weekly openings (shifted by 1)
  const openingWeekly = [
    openingBalance,
    ...closingWeekly.slice(0, 11),
  ];

  // ── Build week objects ────────────────────────────────────────────────────
  const weeks = weekLabels.map((label, i) => {
    const opening = openingWeekly[i] || 0;
    const receipts = receiptsTotals[i] || 0;
    const payments = paymentsTotals[i] || 0;
    const closing = closingWeekly[i] || 0;
    const net = receipts - payments;
    return {
      label,
      opening,
      receipts,
      payments,
      closing,
      net,
      isNegative: closing < 0,
      isTight: closing >= 0 && closing < 100000 && (receipts > 0 || payments > 0),
    };
  });

  // ── Top receipt clients (col 1 = total outstanding) ──────────────────────
  const receipts = [];
  for (let i = receiptsRow + 1; i < paymentsRow; i++) {
    const r = rows[i];
    const name = String(r?.[0] || '').trim();
    if (!name || name.toUpperCase().startsWith('TOTAL')) continue;
    const total = num(r?.[1]);
    const weekly = getWeekly(r);
    if (total > 500 || weekly.some(v => v > 500)) {
      receipts.push({ name, total: total || weekly.reduce((s, v) => s + v, 0), weekly });
    }
  }

  // ── Top payment vendors (col 1 = total due) ───────────────────────────────
  const payments = [];
  const endRow = closingRow >= 0 ? closingRow : paymentsRow + 150;
  for (let i = paymentsRow + 1; i < endRow; i++) {
    const r = rows[i];
    const name = String(r?.[0] || '').trim();
    if (!name || name.toUpperCase().startsWith('TOTAL')) continue;
    const total = num(r?.[1]);
    const weekly = getWeekly(r);
    if (total > 500 || weekly.some(v => v > 500)) {
      payments.push({ name, total: total || weekly.reduce((s, v) => s + v, 0), weekly });
    }
  }

  return {
    currency,
    openingBalance,
    closingBalance,
    weeks,
    receipts: receipts.sort((a, b) => b.total - a.total).slice(0, 25),
    payments: payments.sort((a, b) => b.total - a.total).slice(0, 25),
  };
}

// ─── DERIVE COMPUTED DATA ────────────────────────────────────────────────────
export function computeClientMetrics(jobs, arData, clientMaster, historyJobs = []) {
  const today = new Date();
  const oneWeekAgo = new Date(today - 7 * 24 * 60 * 60 * 1000);
  const twoWeeksAgo = new Date(today - 14 * 24 * 60 * 60 * 1000);
  const threeWeeksAgo = new Date(today - 21 * 24 * 60 * 60 * 1000);

  // Group jobs by client
  const byClient = {};
  jobs.forEach(job => {
    const c = job.client;
    if (!byClient[c]) byClient[c] = [];
    byClient[c].push(job);
  });

  // Build normalized history lookup for cross-file matching
  const normalize = (n) => {
    if (!n) return '';
    return String(n).toUpperCase()
      .replace(/[.\-_,()]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b(LLC|L\.L\.C|INC|LTD|FZE|FZCO|CO|TRADING|GROUP|GLOBAL|ENTERPRISES|COMPANY)\b/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const historyByNorm = {};
  historyJobs.forEach(job => {
    const norm = normalize(job.client);
    if (!historyByNorm[norm]) historyByNorm[norm] = [];
    historyByNorm[norm].push(job);
  });

  // Also group history jobs by client — used for last activity detection
  const historyByClient = {};
  historyJobs.forEach(job => {
    const c = job.client;
    if (!historyByClient[c]) historyByClient[c] = [];
    historyByClient[c].push(job);
  });
  // Merge history clients into byClient so we don't miss clients who closed all jobs
  Object.keys(historyByClient).forEach(c => {
    if (!byClient[normalize(c)]) byClient[c] = [];
  });

  // Group AR by client
  const arByClient = {};
  arData.forEach(row => {
    arByClient[row.client] = row;
  });

  const oneWeekAgoMs = oneWeekAgo.getTime();
  const twoWeeksAgoMs = twoWeeksAgo.getTime();
  const threeWeeksAgoMs = threeWeeksAgo.getTime();

  const clients = Object.keys(byClient).map(clientName => {
    const clientJobs = byClient[clientName];
    const master = clientMaster[clientName] || {};
    const ar = arByClient[clientName] || {};

    // Weekly job counts — createdDate is stored as ms timestamp
    const thisWeek = clientJobs.filter(j => j.createdDate && j.createdDate >= oneWeekAgoMs).length;
    const lastWeek = clientJobs.filter(j => j.createdDate && j.createdDate >= twoWeeksAgoMs && j.createdDate < oneWeekAgoMs).length;
    const weekBefore = clientJobs.filter(j => j.createdDate && j.createdDate >= threeWeeksAgoMs && j.createdDate < twoWeeksAgoMs).length;

    // Active jobs
    const openJobs = clientJobs.filter(j => j.status === 'Initiated');
    const completedJobs = clientJobs.filter(j => j.status === 'Completed');

    // Revenue metrics
    const totalRev = clientJobs.reduce((s, j) => s + j.actRev, 0);
    const totalPL = clientJobs.reduce((s, j) => s + j.pl, 0);
    const gpPct = totalRev > 0 ? (totalPL / totalRev) * 100 : null;

    // Last job date — check both open jobs AND history jobs using normalized name matching
    const jobDates = clientJobs.filter(j => j.createdDate).map(j => j.createdDate);
    const normClient = normalize(clientName);
    const historyMatches = historyByNorm[normClient] || historyByClient[clientName] || [];
    const historyDates = historyMatches.filter(j => j.createdDate).map(j => j.createdDate);
    const allDates = [...jobDates, ...historyDates];
    const lastJobDateMs = allDates.length > 0 ? Math.max(...allDates) : null;
    const lastJobDate = lastJobDateMs ? new Date(lastJobDateMs) : null;
    const daysSinceLastJob = lastJobDate ? daysBetween(lastJobDate, today) : null;

    // AR metrics
    const arOverdue = (ar.d60_90 || 0) + (ar.d90_180 || 0) + (ar.d180_365 || 0) + (ar.gt365 || 0);
    const arTotal = ar.pending || 0;
    const arCritical = (ar.d90_180 || 0) + (ar.d180_365 || 0) + (ar.gt365 || 0);

    // Stale open jobs — open + no revenue + >30 days
    const staleJobs = openJobs.filter(j => j.daysOpen > 30);

    // Attention score (higher = needs more attention)
    let attentionScore = 0;
    if (daysSinceLastJob > 30) attentionScore += 30;
    if (daysSinceLastJob > 60) attentionScore += 30;
    if (arOverdue > 0) attentionScore += Math.min(40, arOverdue / 1000);
    if (arCritical > 0) attentionScore += 20;
    if (staleJobs.length > 0) attentionScore += staleJobs.length * 5;
    if (openJobs.length > 0 && arOverdue > 0) attentionScore += 20; // working for someone who hasn't paid

    // Status badge
    let status = 'green';
    if (attentionScore > 20) status = 'amber';
    if (attentionScore > 50) status = 'red';
    if (daysSinceLastJob > 60 && openJobs.length === 0) status = 'grey'; // gone quiet

    return {
      name: clientName,
      responsible: master.responsible || null,
      phone: master.phone || null,
      expectedFrequency: master.expectedFrequency || null,
      clientStatus: master.clientStatus || 'Active',
      notes: master.notes || '',
      // Job metrics
      openJobs: openJobs.length,
      openJobsValue: openJobs.reduce((s, j) => s + j.estRev, 0),
      completedJobs: completedJobs.length,
      totalJobs: clientJobs.length,
      thisWeek,
      lastWeek,
      weekBefore,
      trend: thisWeek > lastWeek ? 'up' : thisWeek < lastWeek ? 'down' : 'flat',
      lastJobDate,
      daysSinceLastJob,
      staleJobs: staleJobs.length,
      // Revenue
      totalRev,
      totalPL,
      gpPct,
      // AR
      arTotal,
      arOverdue,
      arCritical,
      // Computed
      attentionScore,
      status,
      // Flags
      flags: buildClientFlags(clientName, clientJobs, ar, master, daysSinceLastJob),
    };
  });

  // Sort by attention score descending
  return clients.sort((a, b) => b.attentionScore - a.attentionScore);
}

function buildClientFlags(clientName, jobs, ar, master, daysSinceLastJob) {
  const flags = [];
  const today = new Date(); // eslint-disable-line
  const responsible = master.responsible || null;

  // Stamp responsible person onto every job for Excel export
  const stampedJobs = (jobList) => jobList.map(j => ({
    ...j,
    responsiblePerson: responsible || '—',
    clientOwner: clientName,
  }));

  // Gone quiet
  if (daysSinceLastJob > 30) {
    const severity = daysSinceLastJob > 60 ? 'red' : 'amber';
    flags.push({
      type: 'gone_quiet',
      severity,
      message: `No new job in ${daysSinceLastJob} days`,
      action: 'Schedule client meeting',
    });
  }

  const arCritical = (ar.d90_180 || 0) + (ar.d180_365 || 0) + (ar.gt365 || 0);
  const openJobs = jobs.filter(j => j.status === 'Initiated');
  if (arCritical > 0 && openJobs.length > 0) {
    flags.push({
      type: 'ar_and_active',
      severity: 'red',
      message: `AED ${arCritical.toLocaleString()} overdue 90+ days — ${openJobs.length} jobs still running`,
      action: 'Stop work or get payment commitment',
      jobs: stampedJobs(openJobs),
    });
  }

  // Completed jobs with zero revenue
  const zeroRevCompleted = jobs.filter(j => (j.status === 'Completed' || j.status === 'ForcedClosed') && j.actRev === 0 && j.estRev > 0);
  if (zeroRevCompleted.length > 0) {
    flags.push({
      type: 'zero_revenue',
      severity: 'red',
      message: `${zeroRevCompleted.length} completed job(s) with no revenue recorded`,
      action: 'Check invoicing — work done, money not captured',
      jobs: stampedJobs(zeroRevCompleted),
    });
  }

  // Revenue below estimate
  const completedWithRev = jobs.filter(j => (j.status === 'Completed' || j.status === 'ForcedClosed') && j.estRev > 0 && j.actRev > 0);
  const underBilled = completedWithRev.filter(j => j.actRev < j.estRev * 0.8);
  if (underBilled.length > 0) {
    flags.push({
      type: 'under_billed',
      severity: 'amber',
      message: `${underBilled.length} job(s) invoiced at <80% of estimate`,
      action: 'Review — discounting or estimation problem',
      jobs: stampedJobs(underBilled),
    });
  }

  // Stale open jobs
  const stale = jobs.filter(j => j.status === 'Initiated' && j.daysOpen > 60);
  if (stale.length > 0) {
    flags.push({
      type: 'stale_jobs',
      severity: 'amber',
      message: `${stale.length} open job(s) sitting for 60+ days`,
      action: 'Chase ops to close or invoice',
      jobs: stampedJobs(stale),
    });
  }

  return flags;
}

export function computeWeeklyJobStats(jobs) {
  const today = new Date();
  const weeks = [0, 1, 2, 3, 4].map(w => {
    const start = new Date(today - (w + 1) * 7 * 24 * 60 * 60 * 1000);
    const end = new Date(today - w * 7 * 24 * 60 * 60 * 1000);
    const label = w === 0 ? 'This week' : w === 1 ? 'Last week' : `${w + 1} weeks ago`;

    const opened = jobs.filter(j => j.createdDate && j.createdDate >= start && j.createdDate < end);
    const closed = jobs.filter(j => (j.status === 'Completed' || j.status === 'ForcedClosed') && j.createdDate && j.createdDate >= start && j.createdDate < end);

    return {
      label,
      start,
      end,
      opened: opened.length,
      openedValue: opened.reduce((s, j) => s + j.estRev, 0),
      closed: closed.length,
      closedValue: closed.reduce((s, j) => s + j.actRev, 0),
      gp: closed.reduce((s, j) => s + j.pl, 0),
    };
  });

  return weeks.reverse();
}

export function computeGlobalFlags(jobs, arData, cashflow) {
  const flags = [];
  const today = new Date();
  const todayMs = today.getTime(); // eslint-disable-line

  // End-of-month bunching detector
  const thisMonth = jobs.filter(j => {
    if (!j.createdDate) return false;
    const d = new Date(j.createdDate); // reconstruct from ms timestamp
    return d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
  });
  const lastThreeDays = thisMonth.filter(j => daysBetween(new Date(j.createdDate), today) <= 3);
  if (thisMonth.length > 0 && lastThreeDays.length / thisMonth.length > 0.4) {
    flags.push({
      type: 'month_end_bunching',
      severity: 'amber',
      entity: 'ops',
      message: `${Math.round(lastThreeDays.length / thisMonth.length * 100)}% of this month's jobs opened in the last 3 days`,
      action: 'Investigate — jobs may be backdated or invoicing delayed',
    });
  }

  // Completed but not closed for 30+ days
  const completedNotClosed = jobs.filter(j => j.status === 'Completed' && j.createdDate && daysBetween(new Date(j.createdDate), today) > 30);
  if (completedNotClosed.length > 0) {
    flags.push({
      type: 'completed_not_closed',
      severity: 'amber',
      entity: 'ops',
      message: `${completedNotClosed.length} jobs completed (invoiced) but not formally closed for 30+ days`,
      action: 'Finalise costs and close jobs — P&L incomplete until closed',
      jobs: completedNotClosed,
    });
  }

  // Jobs with cost but no revenue
  const costNoRev = jobs.filter(j => j.actCost > 0 && j.actRev === 0 && j.status === 'Initiated');
  if (costNoRev.length > 5) {
    flags.push({
      type: 'cost_no_revenue',
      severity: 'red',
      entity: 'finance',
      message: `${costNoRev.length} jobs have costs booked but no revenue captured`,
      action: 'Review with ops — cost leaking without invoicing',
      jobs: costNoRev,
    });
  }

  // AR critical
  const criticalAR = arData.filter(r => (r.d90_180 + r.gt365) > 10000);
  if (criticalAR.length > 0) {
    const total = criticalAR.reduce((s, r) => s + r.d90_180 + r.gt365, 0);
    flags.push({
      type: 'critical_ar',
      severity: 'red',
      entity: 'collections',
      message: `AED ${total.toLocaleString()} overdue 90+ days across ${criticalAR.length} clients`,
      action: 'Immediate collection action required',
      clients: criticalAR,
    });
  }

  // Cash position
  if (cashflow && cashflow.closingBalance < 100000) {
    flags.push({
      type: 'low_cash',
      severity: cashflow.closingBalance < 50000 ? 'red' : 'amber',
      entity: 'cash',
      message: `Bank position: AED ${cashflow.closingBalance.toLocaleString()}`,
      action: cashflow.closingBalance < 50000 ? 'Critical — accelerate collections immediately' : 'Watch — chase priority receivables this week',
    });
  }

  return flags;
}
